import { OptionalBooleanProp, ProductProperties } from '@avo/interface/trigger-props';
import { DesktopSectorVm, Flight, MobileSector, MobileSectorVm, Sector } from '@app/entities/flight-results';
import { AirValidationFlightsSector, AirValidationSector, TripOptions } from '@app/entities/air-validation.response';
import { isNullOrUndefined } from '@app/util/util';

export function getFlightData(data: TripOptions): {
  item: AirValidationFlightsSector, flights: AirValidationFlightsSector[], options: AirValidationSector[], fare: string
} {
  const options = data.Sectors ? data.Sectors : null;
  const flights = options && options.length ? options[0].Flights : null;
  const hasFlights = !!flights && flights.length;
  const item = hasFlights ? flights[0] : {} as AirValidationFlightsSector;
  const fareFamilies = hasFlights ? removeEmptyAndDuplicates(flights.map(x => x.FareFamilies)) : '';
  const fareBasis = hasFlights ? removeEmptyAndDuplicates(flights.map(x => x.FareBasis)) : '';
  return { item, flights, options, fare: fareFamilies || fareBasis || null };
}

export function flightAmplitudeProductPropertiesMapper(data: TripOptions): ProductProperties {
  if (data) {
    const { item, flights, options, fare } = getFlightData(data);
    if (isNullOrUndefined(options) || options.length === 0 || isNullOrUndefined(flights) || flights.length === 0) {
      return { 
        TRAVEL_PRODUCT_PROVIDER: data.Provider,
        ...(!isNullOrUndefined(data?.HasPrivateFare)) && { TRAVEL_HAS_MEMBER_RATE: !!data?.HasPrivateFare }
      };
    } else {
      const props = {
        TRAVEL_PRODUCT_CODE: item.OpAirlineCode,
        TRAVEL_PRODUCT_NAME: item.AirlineName,
        TRAVEL_SUB_PRODUCT: fare,
        TRAVEL_PRODUCT_PROVIDER: data.Provider,
        TRAVEL_TOTAL_PRICE: data?.Price?.Total,
      };
      return { 
        ...props,
        ...(!isNullOrUndefined(data?.HasPrivateFare)) && { TRAVEL_HAS_MEMBER_RATE: !!data?.HasPrivateFare }
      };
    }
  } else return {};
}

export function flightAmplitudeProductPropertiesFromResultsMapper(
  options: MobileSector[] | MobileSectorVm[] | Sector[],
  provider: string = '',
  price: number | null = null,
  hasMemberRate?: OptionalBooleanProp
): ProductProperties {
  if (isNullOrUndefined(options) || options.length === 0) {
    return { 
      TRAVEL_PRODUCT_PROVIDER: provider,
      ...(!isNullOrUndefined(hasMemberRate)) && { TRAVEL_HAS_MEMBER_RATE: !!hasMemberRate }
    };
  } else {
    return getFlightsProductPropertiesMapper(options?.[0]?.Flights, provider, price, hasMemberRate);
  }
}

export function flightAmplitudeProductPropertiesFromDesktopResultsMapper(
  options: DesktopSectorVm[],
  provider: string = '',
  price: number | null = null,
  hasMemberRate?: OptionalBooleanProp
): ProductProperties {
  if (isNullOrUndefined(options) || options.length === 0) {
    return { 
      TRAVEL_PRODUCT_PROVIDER: provider,
      ...(!isNullOrUndefined(hasMemberRate)) && { TRAVEL_HAS_MEMBER_RATE: !!hasMemberRate }
    };
  } else {
    return getFlightsProductPropertiesMapper(options[0].Options[0].Flights, provider, price, hasMemberRate);
  }
}

function removeEmptyAndDuplicates(values: string[]): string {
  if (!values) return '';
  return values.filter(x => !isNullOrUndefined(x))
    .reduce((arr, value) => arr.includes(value) ? arr : [...arr, value], [])
    .join(',');
}

function getFlightsProductPropertiesMapper(
  flights: Flight[],
  provider: string = '',
  price: number | null = null,
  hasMemberRate?: OptionalBooleanProp
): ProductProperties {
  if (isNullOrUndefined(flights) || flights.length === 0) return { TRAVEL_PRODUCT_PROVIDER: provider };
  const item = flights[0];
  const fareFamilies = removeEmptyAndDuplicates(flights.map(x => x.FareFamilies));
  const fareBasis = removeEmptyAndDuplicates(flights.map(x => x.FareBasis));
  const props = {
    TRAVEL_PRODUCT_CODE: item.OpAirlineCode,
    TRAVEL_PRODUCT_NAME: item.AirlineName,
    TRAVEL_SUB_PRODUCT: fareFamilies || fareBasis,
    TRAVEL_PRODUCT_PROVIDER: provider,
    TRAVEL_TOTAL_PRICE: price,
  };
  return { 
    ...props,
    ...(!isNullOrUndefined(hasMemberRate)) && { TRAVEL_HAS_MEMBER_RATE: !!hasMemberRate }
  };
}
